import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { MaterialModule } from '../../../material/material.module';
import { UserModel } from '../../../shared/models/user.model';
import { UserService } from '../../../shared/services/user.service';

@Component({
  selector: 'app-scouts',
  imports: [CommonModule, FormsModule, MaterialModule, RouterModule, TranslatePipe],
  templateUrl: './scouts.component.html',
  styleUrl: './scouts.component.css'
})
export class ScoutsComponent implements OnInit {
  scouts: UserModel[] = [];
  filteredScouts: UserModel[] = [];
  searchTerm = '';
  statusFilter: 'all' | 'active' = 'all';
  private activeScoutIds = new Set<string>();
  private pendingToggleIds = new Set<string>();
  isLoading = false;
  loadError = false;
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  constructor(
    private userService: UserService,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.loadScouts();
  }

  back(): void {
    this.location.back();
  }

  loadScouts(): void {
    this.isLoading = true;
    this.loadError = false;

    forkJoin({
      allScouts: this.userService.getAllScouts(),
      activeScouts: this.userService.getAllScoutsActive(),
    })
    .pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
      })
    )
    .subscribe({
      next: ({ allScouts, activeScouts }) => {
        this.scouts = (allScouts as UserModel[]) || [];
        this.activeScoutIds = new Set(
          ((activeScouts as UserModel[]) || [])
            .map((scout) => (scout.UserId || '').toLowerCase())
            .filter((id) => id.length > 0)
        );
        this.applyFilters();
        this.cdr.detectChanges();
      },
      error: () => {
        this.scouts = [];
        this.filteredScouts = [];
        this.activeScoutIds.clear();
        this.loadError = true;
        this.cdr.detectChanges();
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    this.filteredScouts = this.scouts.filter((scout) => {
      if (this.statusFilter === 'active' && !this.isActiveScout(scout)) {
        return false;
      }

      if (!term) {
        return true;
      }

      const name = this.getScoutName(scout).toLowerCase();
      const email = (scout.Email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }

  isActiveScout(scout: UserModel): boolean {
    const userId = (scout.UserId || '').toLowerCase();
    if (!userId) {
      return false;
    }

    return this.activeScoutIds.has(userId);
  }

  isTogglePending(scout: UserModel): boolean {
    const userId = (scout.UserId || '').toLowerCase();
    return !!userId && this.pendingToggleIds.has(userId);
  }

  toggleScoutActive(scout: UserModel, isActive: boolean): void {
    const userId = (scout.UserId || '').trim();
    const normalizedUserId = userId.toLowerCase();

    if (!userId || this.pendingToggleIds.has(normalizedUserId)) {
      return;
    }

    const wasActive = this.activeScoutIds.has(normalizedUserId);

    if (isActive) {
      this.activeScoutIds.add(normalizedUserId);
    } else {
      this.activeScoutIds.delete(normalizedUserId);
    }

    this.pendingToggleIds.add(normalizedUserId);
    this.applyFilters();
    this.cdr.detectChanges();

    this.userService.updateIsenabled(userId, isActive)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.pendingToggleIds.delete(normalizedUserId);
          this.cdr.detectChanges();
        })
      )
      .subscribe({
      next: () => {
        this.cdr.detectChanges();
      },
      error: () => {
        if (wasActive) {
          this.activeScoutIds.add(normalizedUserId);
        } else {
          this.activeScoutIds.delete(normalizedUserId);
        }
        this.applyFilters();
        this.cdr.detectChanges();
      }
    });
  }

  openScoutDetails(scout: UserModel): void {
    if (!scout.UserId) {
      return;
    }

    this.router.navigate(['/admin/users', scout.UserId]);
  }

  openNewScout(): void {
    this.router.navigate(['/admin/scouts/new']);
  }

  getScoutName(scout: UserModel): string {
    const firstName = (scout.FirstName || '').trim();
    const lastName = (scout.LastName || '').trim();

    if (lastName && firstName) {
      return `${lastName}, ${firstName}`;
    }

    if (lastName) {
      return lastName;
    }

    if (firstName) {
      return firstName;
    }

    return scout.Email || 'Unknown user';
  }
}
